import { useEffect, useCallback, useRef } from 'react';

import { useAppStore } from '@/store';
import { useToast } from './useToast';
import { isDesktop } from '@/consts/device';

import type { IAppStore } from '@/store';

const selector = (store: IAppStore) => ({
  localNotification: store.localNotification,
  setLocalNotification: store.setLocalNotification,
  setScreen: store.setScreen,
  setRightPanelTab: store.setRightPanelTab,
  setPendingLightboxFileId: store.setPendingLightboxFileId,
  setIsResultMode: store.setIsResultMode,
});

/**
 * Hook that watches for local notifications and shows a toast
 * when user is not on the files/loading screen.
 * Clicking the CTA navigates to files and opens the result in the lightbox.
 */
const useLocalNotification = () => {
  const { toast, showToast, hideToast } = useToast({ actionDuration: 6000 });

  const {
    localNotification,
    setLocalNotification,
    setScreen,
    setRightPanelTab,
    setPendingLightboxFileId,
    setIsResultMode,
  } = useAppStore(selector);

  // Capture driveUid in a ref so handleViewResult can access it
  // after localNotification is cleared from the store
  const pendingDriveUidRef = useRef<string | null>(null);

  const handleViewResult = useCallback(() => {
    hideToast();
    setScreen('files');
    if (isDesktop) {
      setRightPanelTab('creations');
    }
    if (pendingDriveUidRef.current) {
      setPendingLightboxFileId(pendingDriveUidRef.current);
      setIsResultMode(true);
      pendingDriveUidRef.current = null;
    }
    setLocalNotification(null);
  }, [
    hideToast,
    setScreen,
    setRightPanelTab,
    setPendingLightboxFileId,
    setIsResultMode,
    setLocalNotification,
  ]);

  useEffect(() => {
    if (localNotification) {
      // Capture driveUid before clearing notification
      pendingDriveUidRef.current = localNotification.driveUid ?? null;

      showToast({
        message: 'Your creation is ready!',
        type: 'success',
        action: {
          label: 'View',
          onClick: handleViewResult,
        },
      });

      // Clear the notification from store after showing
      setLocalNotification(null);
    }
  }, [localNotification, showToast, handleViewResult, setLocalNotification]);

  return { toast, hideToast };
};

export { useLocalNotification };
